import { Body, Heading } from '@assembly-js/design-system';
import { Editor } from '@tiptap/core';
import { FormikProps } from 'formik';
import React, { useContext, useMemo } from 'react';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import { TextField } from 'src/components/TextField';
import { AlphaNumericRegex } from 'src/constants';
import { RouteContext } from 'src/context';
import { FormModal } from 'src/legacy/components/Modals';
import { PaymentRichTextEditor } from 'src/routes/payments/components/PaymentRichTextEditor';
import { ServiceFileDropzone } from 'src/routes/payments/services/components/ServiceFileDropzone';
import { Product } from 'src/services/api/productsApi';
import { getPublicImageURL } from 'src/utils/ImageUtils';

interface Props {
  open: boolean;
  onCloseModal: () => void;
  service?: Product;
  onUpdateService: (service: ServiceDetailsSchema) => void;
}
export const serviceDetailSchema = z.object({
  id: z.string(),
  name: z
    .string({ required_error: 'Name required' })
    .refine(
      (val: string) => AlphaNumericRegex.test(val.replaceAll(' ', '')),
      'Name cannot be only numbers',
    ),
  description: z.string().optional(),
  imageUrls: z.array(z.string()),
});

export type ServiceDetailsSchema = z.infer<typeof serviceDetailSchema>;

const useForm = () => {
  const FormRenderer = ({
    touched,
    errors,
    handleBlur,
    handleChange,
    values,
    setFieldValue,
  }: FormikProps<ServiceDetailsSchema>) => {
    const handleContentChange = (editor: Editor) => {
      setFieldValue('description', editor.getHTML());
    };

    return (
      <div className="flex flex-col gap-4">
        <TextField
          label="Name"
          fullWidth
          sizeVariant="medium"
          name="name"
          id="input-lineitem-name"
          onBlur={handleBlur}
          onChange={handleChange}
          value={values.name || ''}
          variant="outlined"
          helperText={
            !errors.name ? (
              <Body className="text-[13px] text-gray-small">
                For optimal search visibility, keep titles less than 60
                characters.
              </Body>
            ) : (
              touched.name && errors.name
            )
          }
          error={Boolean(touched.name && errors.name)}
        />

        <div>
          <div className="flex items-center mb-1">
            <Heading className="text-black-heading">Description</Heading>
            <Heading className=" text-gray-small">&nbsp;- Optional</Heading>
          </div>
          <div>
            <PaymentRichTextEditor
              description={values.description || ''}
              onContentChange={handleContentChange}
            />
          </div>
          <Body className="text-[13px] text-gray-small text-right">
            For optimal search visibility, keep descriptions less than 155
            characters.
          </Body>
        </div>

        <div>
          <div className="flex items-center mb-1">
            <Heading className="text-black-heading">Image</Heading>
          </div>
          <ServiceFileDropzone
            imageUrl={getPublicImageURL(String(values.imageUrls || ''), {
              resize: { width: 300, height: 300 },
            })}
          />
          <div className="flex justify-end">
            <div className="flex justify-end">
              <>
                <Body className=" text-[13px] text-gray-small text-right">
                  {'Recommended at least 448x448 pixels.'}
                </Body>
              </>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return {
    FormRenderer,
    validationScheme: toFormikValidationSchema(serviceDetailSchema),
  };
};

export const EditServiceDetails: React.FC<Props> = ({
  open,
  onCloseModal,
  service,
  onUpdateService,
}) => {
  const { pathname } = useContext(RouteContext);
  const serviceId = pathname.split('/').pop();

  const initialFormValue: ServiceDetailsSchema = useMemo(
    () => ({
      id: service?.id ?? '',
      name: service?.name ?? '',
      description: service?.description ?? '',
      imageUrls: service?.imageUrls ?? [],
    }),
    [service],
  );

  const handleCloseModal = () => {
    onCloseModal();
  };

  const handleUpdateService = async (values: ServiceDetailsSchema) => {
    if (!serviceId) return;
    await onUpdateService(values);
  };

  return (
    <FormModal
      title={'Edit details'}
      buttonLabel={'Update'}
      initialFormValue={initialFormValue}
      useFormHook={useForm}
      handleSave={handleUpdateService}
      open={open}
      onClose={handleCloseModal}
    />
  );
};
